import { TextSelection } from '@tiptap/pm/state';
import { Node_Type } from 'ricos-schema';
import codeBlockDataDefaults from 'ricos-schema/dist/statics/code_block.defaults.json';
import { createRicosNodeExtension, withBreakoutContainer } from 'wix-rich-content-plugin-commons';
import { ROOT } from './dataHooks.js';

function styleInject(css, ref) {
  if ( ref === void 0 ) ref = {};
  var insertAt = ref.insertAt;
  var isTest = typeof process !== 'undefined' && process.env && process.env['NODE_ENV'] === 'test';

  if (isTest || !css || typeof document === 'undefined') { return; }

  var head = document.head || document.getElementsByTagName('head')[0];
  var style = document.createElement('style');
  style.type = 'text/css';

  if (insertAt === 'top') {
    if (head.firstChild) {
      head.insertBefore(style, head.firstChild);
    } else {
      head.appendChild(style);
    }
  } else {
    head.appendChild(style);
  }

  if (style.styleSheet) {
    style.styleSheet.cssText = css;
  } else {
    style.appendChild(document.createTextNode(css));
  }
}

var css_248z = ".HBUpG,.glob_fontElementMap{font-family:var(--ricos-font-family,unset)}[dir] .zlDfZ{margin:15px 18px}@supports not (contain:inline-size){@media only screen and (max-width:640px){[dir] .zlDfZ{margin:var(--ricos-custom-code-block-margin,15px calc(18px + 5%))}}}@container (width < 640px){[dir] .zlDfZ{margin:var(--ricos-custom-code-block-margin,15px calc(18px + 5%))}}._4F-SW,.wCpyT{font-family:Inconsolata,Menlo,Consolas,monospace;font-size:var(--ricos-custom-code-block-font-size,16px);line-height:var(--ricos-custom-code-block-line-height,26px);white-space:pre-wrap}[dir] ._4F-SW,[dir] .wCpyT{background-color:rgba(var(--ricos-fallback-color-tuple,0,0,0),.06);margin:var(--ricos-custom-code-block-margin,15px 18px);padding:var(--ricos-custom-code-block-padding,2px 25px)}._4F-SW code,.wCpyT code{font-family:Inconsolata,Menlo,Consolas,monospace;font-size:var(--ricos-custom-code-block-font-size,16px)}[dir] .qBf2S ._4F-SW,[dir] .qBf2S .wCpyT{margin:0}";
var styles = {"fontElementMap":"HBUpG","blockStyleSpacing":"zlDfZ","codeBlock":"wCpyT zlDfZ","code":"_4F-SW zlDfZ","renderedInTable":"qBf2S"};
styleInject(css_248z);

var __defProp = Object.defineProperty;
var __defProps = Object.defineProperties;
var __getOwnPropDescs = Object.getOwnPropertyDescriptors;
var __getOwnPropSymbols = Object.getOwnPropertySymbols;
var __hasOwnProp = Object.prototype.hasOwnProperty;
var __propIsEnum = Object.prototype.propertyIsEnumerable;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp.call(b, prop))
      __defNormalProp(a, prop, b[prop]);
  if (__getOwnPropSymbols)
    for (var prop of __getOwnPropSymbols(b)) {
      if (__propIsEnum.call(b, prop))
        __defNormalProp(a, prop, b[prop]);
    }
  return a;
};
var __spreadProps = (a, b) => __defProps(a, __getOwnPropDescs(b));
const backtickInputRegex = new RegExp("^```(?<language>[a-z]*)?[\\s\\n]$");
const tildeInputRegex = new RegExp("^~~~(?<language>[a-z]*)?[\\s\\n]$");
const tiptapExtensions = [
  createRicosNodeExtension({
    groups: ["text-container", "shortcuts-enabled"],
    type: "html-node",
    name: Node_Type.CODE_BLOCK,
    reconfigure: (config, _extensions, _props, settings, services) => __spreadProps(__spreadValues({}, config), {
      addOptions() {
        return __spreadValues({
          services,
          languageClassPrefix: "language-",
          HTMLAttributes: {
            class: styles.code
          }
        }, settings);
      }
    }),
    createExtensionConfig({ Plugin, PluginKey, textblockTypeInputRule, mergeAttributes }) {
      return {
        name: this.name,
        content: "text*",
        group: "block",
        code: true,
        defining: true,
        priority: 101,
        addAttributes() {
          return __spreadProps(__spreadValues({}, codeBlockDataDefaults), {
            language: {
              default: null,
              renderHTML: (attributes) => {
                if (!attributes.language) {
                  return null;
                }
                return {
                  class: this.options.languageClassPrefix + attributes.language
                };
              }
            }
          });
        },
        parseHTML() {
          return [
            {
              tag: "pre",
              preserveWhitespace: "full"
            }
          ];
        },
        renderHTML({ HTMLAttributes }) {
          const allAttributes = mergeAttributes(this.options.HTMLAttributes, HTMLAttributes, {
            "data-hook": ROOT,
            dir: "auto"
          });
          return withBreakoutContainer(["pre", allAttributes, 0]);
        },
        addCommands() {
          return {
            setCodeBlock: (attributes) => ({ commands }) => {
              return commands.updateTextNode(this.name, attributes);
            },
            toggleCodeBlock: (attributes) => ({ commands }) => {
              return commands.toggleTextNode(this.name, Node_Type.PARAGRAPH, attributes);
            }
          };
        },
        addKeyboardShortcuts() {
          const newLine = ({ editor }) => {
            const { state } = editor;
            const { selection } = state;
            const { $from, empty } = selection;
            if (!empty || $from.parent.type !== this.type) {
              return false;
            }
            const isAtEnd = $from.parentOffset === $from.parent.nodeSize - 2;
            const endsWithDoubleNewline = $from.parent.textContent.endsWith("\n\n");
            if (!isAtEnd || !endsWithDoubleNewline) {
              return false;
            }
            return editor.chain().command(({ tr }) => {
              tr.delete($from.pos - 2, $from.pos);
              return true;
            }).exitCode().run();
          };
          const tiptapKeyboardShortcuts = {
            Enter: newLine
          };
          const newLineShortcuts = [];
          this.options.services.shortcuts.shortcuts.forEach((shortcut) => {
            if (shortcut.shortcut.name === "NewLine") {
              newLineShortcuts.push(shortcut);
            }
          });
          newLineShortcuts.forEach((newLineShortcut) => {
            const modifier = newLineShortcut.keys.macOs.modifiers[0];
            const key = newLineShortcut.keys.macOs.key;
            const shortcutKey = modifier ? `${modifier}-${key}` : key;
            tiptapKeyboardShortcuts[shortcutKey] = newLine;
          });
          return __spreadValues({
            // 'Mod-Alt-c': () => this.editor.commands.toggleCodeBlock(),
            // remove code block when at start of document or code block is empty
            Backspace: () => {
              const { empty, $anchor } = this.editor.state.selection;
              const isAtStart = $anchor.pos === 1;
              if (!empty || $anchor.parent.type.name !== this.name) {
                return false;
              }
              if (isAtStart || !$anchor.parent.textContent.length) {
                return this.editor.commands.normalizeNodes();
              }
              return false;
            }
          }, tiptapKeyboardShortcuts);
        },
        addInputRules() {
          return [
            textblockTypeInputRule({
              find: backtickInputRegex,
              type: this.type,
              getAttributes: ({ groups }) => groups
            }),
            textblockTypeInputRule({
              find: tildeInputRegex,
              type: this.type,
              getAttributes: ({ groups }) => groups
            })
          ];
        },
        addProseMirrorPlugins() {
          return [
            // this plugin creates a code block for pasted content from VS Code
            // we can also detect the copied code language
            new Plugin({
              key: new PluginKey("codeBlockVSCodeHandler"),
              props: {
                handlePaste: (view, event) => {
                  if (!event.clipboardData) {
                    return false;
                  }
                  if (this.editor.isActive(this.type.name)) {
                    return false;
                  }
                  const text = event.clipboardData.getData("text/plain");
                  const vscode = event.clipboardData.getData("vscode-editor-data");
                  const vscodeData = vscode ? JSON.parse(vscode) : void 0;
                  const language = vscodeData == null ? void 0 : vscodeData.mode;
                  if (!text || !language) {
                    return false;
                  }
                  const { tr } = view.state;
                  tr.replaceSelectionWith(this.type.create({ language }));
                  tr.setSelection(
                    TextSelection.near(tr.doc.resolve(Math.max(0, tr.selection.from - 2)))
                  );
                  tr.insertText(text.replace(/\r\n?/g, "\n"));
                  tr.setMeta("paste", true);
                  view.dispatch(tr);
                  return true;
                }
              }
            })
          ];
        }
      };
    }
  })
];

export { tiptapExtensions };
//# sourceMappingURL=tiptap.js.map
